import type { AgentId } from './types.ts';

/**
 * Models the phone can pick, across every agent backend.
 *
 * A model id is the only thing the UI sends, and it decides which CLI runs the
 * turn: `agent` on each row is what the runner dispatches on. Ids are
 * therefore unique across backends even where the underlying model is not -
 * Cursor's `claude-opus-5` (Opus 5 billed through Cursor) and Claude Code's
 * `claude-code-opus` (Opus 5.5 through your Anthropic plan) are different rows.
 *
 * Claude rows name the exact version in their label (`Opus 5.5`, not `Opus`;
 * the provider badge next to it already says which plan pays), and
 * `CLAUDE_MODEL_ARGS` pins the CLI to that same version, so what the phone
 * shows is what ran.
 *
 * Effort is a separate Settings control, resolved per backend:
 *   - cursor: to a real catalog id from `cursor-agent --list-models`
 *     (e.g. `cursor-grok-4.6-medium`). The bracket form `family[effort=…]`
 *     is rejected by the CLI.
 *   - claude: to a `MAX_THINKING_TOKENS` budget in the child's environment,
 *     because the CLI has no effort flag.
 *   - grok: to the CLI's `--effort` flag (`low` / `medium` / `high`).
 *   - opencode: to the CLI's `--variant` flag (`low` / `medium` / `high`).
 *   - openai: to Codex `-c model_reasoning_effort=…` (`low` / `medium` / `high`).
 *
 * Composer has no effort rungs — it calibrates itself.
 */
export const AGENTS = [
  { id: 'cursor', label: 'Cursor' },
  { id: 'claude', label: 'Claude Code' },
  { id: 'grok', label: 'Grok' },
  { id: 'opencode', label: 'OpenCode' },
  { id: 'openai', label: 'OpenAI' },
] as const;

/**
 * Phone pickers and the home usage card skip these backends for now. Existing
 * chats still run them; the catalog stays so we can turn them back on.
 */
export const HIDDEN_AGENTS = ['grok', 'opencode'] as const satisfies readonly AgentId[];

const HIDDEN_AGENT_IDS = new Set<AgentId>(HIDDEN_AGENTS);

export function isHiddenAgent(agent: AgentId): boolean {
  return HIDDEN_AGENT_IDS.has(agent);
}

export const VISIBLE_AGENTS = AGENTS.filter((a) => !isHiddenAgent(a.id));

/** Next backend the phone offers, walking past hidden ones. */
export function nextVisibleAgent(agent: AgentId): (typeof AGENTS)[number] {
  return nextAgentAmong(agent, VISIBLE_AGENTS);
}

/** Next backend in catalog order that is in `among`, wrapping around. */
export function nextAgentAmong(
  agent: AgentId,
  among: readonly (typeof AGENTS)[number][],
): (typeof AGENTS)[number] {
  const i = AGENTS.findIndex((a) => a.id === agent);
  for (let step = 1; step <= AGENTS.length; step++) {
    const next = AGENTS[(Math.max(i, 0) + step) % AGENTS.length]!;
    if (among.some((a) => a.id === next.id)) return next;
  }
  return among[0] ?? VISIBLE_AGENTS[0] ?? AGENTS[0]!;
}

export const EFFORTS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
] as const;

export type EffortId = (typeof EFFORTS)[number]['id'];

const ALL_EFFORTS = ['low', 'medium', 'high'] as const;

export const MODELS = [
  { id: 'composer-2.5', agent: 'cursor', label: 'Composer 2.5', efforts: [] as const },
  {
    id: 'cursor-grok-4.6',
    agent: 'cursor',
    label: 'Grok 4.6',
    efforts: ALL_EFFORTS,
  },
  { id: 'claude-opus-5', agent: 'cursor', label: 'Claude Opus 5', efforts: ALL_EFFORTS },
  { id: 'claude-code-opus', agent: 'claude', label: 'Opus 5.5', efforts: ALL_EFFORTS },
  { id: 'claude-code-sonnet', agent: 'claude', label: 'Sonnet 5', efforts: ALL_EFFORTS },
  { id: 'claude-code-haiku', agent: 'claude', label: 'Haiku 4.5', efforts: ALL_EFFORTS },
  { id: 'grok-4.6', agent: 'grok', label: '4.6', efforts: ALL_EFFORTS },
  {
    id: 'opencode-grok-4.6',
    agent: 'opencode',
    label: 'Grok 4.6',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'opencode-glm-5.3-flash',
    agent: 'opencode',
    label: 'GLM-5.3-Flash',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'opencode-qwen3.8-flash',
    agent: 'opencode',
    label: 'Qwen3.8 Flash',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'opencode-glm-5.3',
    agent: 'opencode',
    label: 'GLM-5.3',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'openai-gpt-6-astra',
    agent: 'openai',
    label: 'GPT-6 Astra',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'openai-gpt-6-sol',
    agent: 'openai',
    label: 'GPT-6 Sol',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'openai-gpt-6-luna',
    agent: 'openai',
    label: 'GPT-6 Luna',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'openai-gpt-5.6-sol',
    agent: 'openai',
    label: 'GPT-5.6 Sol',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'openai-gpt-5.6-terra',
    agent: 'openai',
    label: 'GPT-5.6 Terra',
    efforts: ALL_EFFORTS,
  },
  {
    id: 'openai-gpt-5.6-luna',
    agent: 'openai',
    label: 'GPT-5.6 Luna',
    efforts: ALL_EFFORTS,
  },
] as const satisfies readonly {
  id: string;
  agent: AgentId;
  label: string;
  efforts: readonly EffortId[];
}[];

/** Ids this build ships with. Discovery can add more at runtime. */
export type CatalogModelId = (typeof MODELS)[number]['id'];

/**
 * A model id. The shipped ids are suggested by the editor, but any id a
 * provider's CLI reports is legal too — see `setDiscoveredModels`.
 */
export type ModelId = CatalogModelId | (string & {});

/** One row of the catalog, shipped or discovered from a provider's CLI. */
export interface ModelRow {
  id: string;
  agent: AgentId;
  label: string;
  efforts: readonly EffortId[];
  /**
   * The CLI `--model` value, when it differs from the id. Discovered rows
   * carry the provider's own spelling here; shipped rows leave it out and go
   * through the per-backend maps below.
   */
  arg?: string;
}

let discovered: readonly ModelRow[] = [];

/**
 * Teach the catalog about models a provider's CLI reported (More → Models).
 * Ids that collide with a shipped row are ignored, so the curated labels and
 * effort rungs win.
 */
export function setDiscoveredModels(rows: readonly ModelRow[]): void {
  const shipped = new Set<string>(MODELS.map((m) => m.id));
  const seen = new Set<string>();
  discovered = rows.filter((row) => {
    if (shipped.has(row.id) || seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}

/** Everything pickable right now: shipped rows first, then discovered ones. */
export function allModels(): readonly ModelRow[] {
  return discovered.length === 0 ? MODELS : [...MODELS, ...discovered];
}

export const DEFAULT_MODEL: ModelId = 'composer-2.5';
export const DEFAULT_AGENT: AgentId = 'cursor';

/** Fallback when a model supports effort but nothing is stored. */
export const DEFAULT_EFFORT: EffortId = 'high';

/**
 * `--model` values for the claude CLI. Pinned to exact model ids rather than
 * the `opus` / `sonnet` / `haiku` aliases: the labels name a version, so the
 * run has to be that version — an alias would silently move under the label
 * on release day. Bumping a Claude model means editing the id and the label
 * here together.
 */
const CLAUDE_MODEL_ARGS: Record<string, string> = {
  'claude-code-opus': 'claude-opus-5-5',
  'claude-code-sonnet': 'claude-sonnet-5',
  'claude-code-haiku': 'claude-haiku-4-5',
};

/** `--model` values for the opencode CLI: `provider/model` as models.dev spells them. */
const OPENCODE_MODEL_ARGS: Record<string, string> = {
  'opencode-grok-4.6': 'opencode-go/grok-4.6',
  'opencode-glm-5.3-flash': 'opencode-go/glm-5.3-flash',
  'opencode-qwen3.8-flash': 'opencode-go/qwen3.8-flash',
  'opencode-glm-5.3': 'opencode-go/glm-5.3',
};

/** `--model` values for the Codex CLI. */
const OPENAI_MODEL_ARGS: Record<string, string> = {
  'openai-gpt-6-astra': 'gpt-6-astra',
  'openai-gpt-6-sol': 'gpt-6-sol',
  'openai-gpt-6-luna': 'gpt-6-luna',
  'openai-gpt-5.6-sol': 'gpt-5.6-sol',
  'openai-gpt-5.6-terra': 'gpt-5.6-terra',
  'openai-gpt-5.6-luna': 'gpt-5.6-luna',
};

/** Effort rungs as an extended-thinking budget for Claude Code. */
const THINKING_TOKENS: Record<EffortId, number> = {
  low: 4_000,
  medium: 10_000,
  high: 31_999,
};

/** Older Settings values baked effort into the model id. */
const LEGACY_MODELS: Record<string, ModelId> = {
  'cursor-grok-4.6-high': 'cursor-grok-4.6',
  'claude-opus-5-high': 'claude-opus-5',
  'opencode-gpt-5.6-sol': 'opencode-grok-4.6',
  'opencode-claude-opus-5': 'opencode-grok-4.6',
  'opencode-claude-sonnet-5': 'opencode-grok-4.6',
  'openai-gpt-5.3-codex': 'openai-gpt-5.6-sol',
};

const EFFORT_IDS = new Set<string>(EFFORTS.map((e) => e.id));

export function canonicalizeModel(value: string): ModelId | null {
  if (allModels().some((m) => m.id === value)) return value;
  return LEGACY_MODELS[value] ?? null;
}

export function isModelId(value: unknown): value is ModelId {
  return typeof value === 'string' && canonicalizeModel(value) !== null;
}

export function isEffortId(value: unknown): value is EffortId {
  return typeof value === 'string' && EFFORT_IDS.has(value);
}

export function isAgentId(value: unknown): value is AgentId {
  return typeof value === 'string' && AGENTS.some((a) => a.id === value);
}

/** Which CLI runs a turn using this model. Unknown ids fall back to cursor. */
export function agentForModel(model: string): AgentId {
  const id = canonicalizeModel(model);
  return allModels().find((m) => m.id === id)?.agent ?? DEFAULT_AGENT;
}

export function modelsForAgent(agent: AgentId): readonly ModelRow[] {
  return allModels().filter((m) => m.agent === agent);
}

/**
 * The backend's models narrowed to the phone's allowlist (More → Models).
 * A null allowlist means "everything in the catalog".
 */
export function enabledModelsForAgent(
  agent: AgentId,
  allowed?: readonly string[] | null,
): readonly ModelRow[] {
  const list = modelsForAgent(agent);
  if (!allowed) return list;
  return list.filter((m) => allowed.includes(m.id));
}

/**
 * Backends the phone offers. Without an allowlist that is the visible set;
 * with one it is whatever the allowlist names, so turning a hidden backend's
 * model on in More → Models brings that backend back.
 */
export function enabledAgents(allowed?: readonly string[] | null): readonly (typeof AGENTS)[number][] {
  if (!allowed) return VISIBLE_AGENTS;
  return AGENTS.filter((a) => enabledModelsForAgent(a.id, allowed).length > 0);
}

export function effortsFor(model: string): readonly EffortId[] {
  const id = canonicalizeModel(model);
  return allModels().find((m) => m.id === id)?.efforts ?? [];
}

export function defaultEffortFor(model: string): EffortId | null {
  const options = effortsFor(model);
  if (options.length === 0) return null;
  if ((options as readonly string[]).includes(DEFAULT_EFFORT)) return DEFAULT_EFFORT;
  return options[0] ?? null;
}

export function isEffortForModel(model: string, effort: unknown): effort is EffortId {
  return typeof effort === 'string' && (effortsFor(model) as readonly string[]).includes(effort);
}

/** Pick a legal effort for this family, or null when the family has none. */
export function coerceEffort(model: string, effort: string | null | undefined): EffortId | null {
  if (isEffortForModel(model, effort)) return effort;
  return defaultEffortFor(model);
}

/** CLI `--model` value: a real `--list-models` id, never the bracket form. */
export function resolveCursorModel(model: string, effort?: string | null): string {
  const family = canonicalizeModel(model) ?? model;
  const rung = coerceEffort(family, effort);
  if (!rung) return family;
  return `${family}-${rung}`;
}

/**
 * The `--model` argument for whichever CLI this model belongs to. Effort is
 * folded in for cursor, carried in the environment for claude, passed as
 * `--effort` for grok, `--variant` for opencode, and `-c
 * model_reasoning_effort=…` for openai, so the halves have to be read
 * together — see `thinkingBudget`.
 */
export function resolveModelArg(model: string, effort?: string | null): string {
  const family = canonicalizeModel(model) ?? model;
  // A discovered row already carries the provider's own spelling, including
  // whatever effort suffix that provider bakes into the id.
  const arg = allModels().find((m) => m.id === family)?.arg;
  if (arg) return arg;
  const agent = agentForModel(family);
  if (agent === 'claude') return CLAUDE_MODEL_ARGS[family] ?? family;
  if (agent === 'grok') return family;
  if (agent === 'opencode') return OPENCODE_MODEL_ARGS[family] ?? family;
  if (agent === 'openai') return OPENAI_MODEL_ARGS[family] ?? family;
  return resolveCursorModel(family, effort);
}

/** `MAX_THINKING_TOKENS` for a claude run, or null when there is no rung. */
export function thinkingBudget(model: string, effort?: string | null): number | null {
  const rung = coerceEffort(canonicalizeModel(model) ?? model, effort);
  return rung ? THINKING_TOKENS[rung] : null;
}

/** Human label for the homescreen and headers, e.g. `Grok 4.6 · High`. */
export function labelForModelEffort(
  model: string | null | undefined,
  effort: string | null | undefined,
): string {
  const family = resolvedModel(model);
  const modelRow = allModels().find((m) => m.id === family);
  const parts: string[] = [modelRow?.label ?? family];
  const rung = coerceEffort(family, effort);
  if (rung) {
    const effortRow = EFFORTS.find((e) => e.id === rung);
    parts.push(effortRow?.label ?? rung);
  }
  return parts.join(' · ');
}

/** Catalog family for a stored session, falling back to the default. */
export function resolvedModel(model: string | null | undefined): ModelId {
  return (model && canonicalizeModel(model)) || DEFAULT_MODEL;
}

export function modelLabel(model: string | null | undefined): string {
  const family = resolvedModel(model);
  return allModels().find((m) => m.id === family)?.label ?? family;
}

export function effortLabel(effort: EffortId): string {
  return EFFORTS.find((e) => e.id === effort)?.label ?? effort;
}

export type PrefAxis = 'agent' | 'model' | 'effort';

/**
 * Advance one homescreen badge. Agent cycles backends, model stays inside
 * the current backend, effort walks the family's rungs.
 */
export function rotateSessionPrefs(
  model: string | null | undefined,
  effort: string | null | undefined,
  axis: PrefAxis,
  allowed?: readonly string[] | null,
): { model: ModelId; effort: EffortId | null } {
  const current = resolvedModel(model);
  const currentEffort = coerceEffort(current, effort);
  // An allowlist that turned everything off would leave nothing to rotate to.
  const list = enabledAgents(allowed).length > 0 ? allowed : null;
  if (axis === 'agent') {
    const nextAgent = nextAgentAmong(agentForModel(current), enabledAgents(list));
    const nextModel = (enabledModelsForAgent(nextAgent.id, list)[0] ?? modelsForAgent(nextAgent.id)[0]!).id;
    return { model: nextModel, effort: coerceEffort(nextModel, currentEffort) };
  }
  if (axis === 'model') {
    const agent = agentForModel(current);
    const models = enabledModelsForAgent(agent, list);
    const family = models.length > 0 ? models : modelsForAgent(agent);
    const i = Math.max(0, family.findIndex((m) => m.id === current));
    const nextModel = family[(i + 1) % family.length]!.id;
    return { model: nextModel, effort: coerceEffort(nextModel, currentEffort) };
  }
  const rungs = effortsFor(current);
  if (rungs.length === 0) return { model: current, effort: null };
  const cur = currentEffort ?? rungs[0]!;
  const i = Math.max(0, rungs.indexOf(cur));
  return { model: current, effort: rungs[(i + 1) % rungs.length]! };
}
